import type { ComponentType } from "react";
import { BestAgeForNewbornPhotos } from "../BestAgeForNewbornPhotos";
import { HowToPrepareForNewbornSession } from "../HowToPrepareForNewbornSession";
import { WhatHappensDuringNewbornSession } from "../WhatHappensDuringNewbornSession";
import { BestTimeForMaternityPhotos } from "../BestTimeForMaternityPhotos";
import { WhatToWearForMaternityPhotos } from "../WhatToWearForMaternityPhotos";
import { HowToPrepareForMaternitySession } from "../HowToPrepareForMaternitySession";
import { BestAgeForBabyPhotos } from "../BestAgeForBabyPhotos";
import { WhatHappensDuringBabySession } from "../WhatHappensDuringBabySession";
import { HowToPrepareForBabySession } from "../HowToPrepareForBabySession";
import { BestTimeOfDayForFamilyPhotos } from "../BestTimeOfDayForFamilyPhotos";
import { WhatToWearForFamilyPhotos } from "../WhatToWearForFamilyPhotos";
import { HowToPrepareKidsForFamilyPhotos } from "../HowToPrepareKidsForFamilyPhotos";
import { HowToPrepareForFamilySession } from "../HowToPrepareForFamilySession";
import { NewbornPhotographySafety } from "../NewbornPhotographySafety";
import newbornPhotographySafetyImage from "../../../assets/images/newborn-photography-safety-supported-pose.webp";
import { InHomeNewbornPhotography } from "../InHomeNewbornPhotography";
import inHomeNewbornImage from "../../../assets/images/newborn-twins-heart-prop-portrait.webp";
import { resourceReadingTimes } from "./resourceReadingTimes";

export type ResourceCategory = "newborn" | "baby" | "maternity" | "family" | "charlotte" | "faq";
export type ResourceFeatureSeason = "winter-holidays" | "spring" | "summer" | "fall";

export interface ResourceArticle {
  category: ResourceCategory;
  component: ComponentType;
  description: string;
  eyebrow: string;
  publishedAt: string;
  updatedAt?: string;
  readTime: string;
  slug: string;
  title: string;
  featuredSeasons?: ResourceFeatureSeason[];
  image?: string;
  imageAlt?: string;
  seoDescription?: string;
  seoTitle?: string;
}

const resourceArticleDefinitions: Omit<ResourceArticle, "readTime">[] = [
  {
    category: "family",
    component: HowToPrepareForFamilySession,
    description: "A complete, low-stress plan for organizing your family, belongings and schedule before photo day.",
    eyebrow: "SESSION PREP",
    publishedAt: "2026-08-01",
    updatedAt: "2026-08-05",
    slug: "how-to-prepare-for-your-family-photo-session",
    title: "How to Prepare for Your Family Photo Session",
  },
  {
    category: "family",
    component: BestTimeOfDayForFamilyPhotos,
    description: "How to choose beautiful light without ignoring naps, meals and your children’s happiest hours.",
    eyebrow: "SESSION TIMING",
    publishedAt: "2026-07-23",
    updatedAt: "2026-08-04",
    slug: "best-time-of-day-for-family-photos",
    title: "Best Time of Day for Family Photos",
  },
  {
    category: "family",
    component: WhatToWearForFamilyPhotos,
    description: "A simple approach to coordinating colors, textures and personalities without matching exactly.",
    eyebrow: "STYLING GUIDE",
    publishedAt: "2026-07-26",
    updatedAt: "2026-08-04",
    slug: "what-to-wear-for-family-photos",
    title: "What to Wear for Family Photos",
  },
  {
    category: "family",
    component: HowToPrepareKidsForFamilyPhotos,
    description: "A low-pressure plan that helps children arrive comfortable, connected and ready to play.",
    eyebrow: "HAPPY KIDS",
    publishedAt: "2026-07-29",
    updatedAt: "2026-08-04",
    slug: "how-to-prepare-kids-for-family-photos",
    title: "How to Prepare Kids for Family Photos",
  },
  {
    category: "baby",
    component: BestAgeForBabyPhotos,
    description: "A milestone-by-milestone guide to choosing the right age for baby portraits.",
    eyebrow: "MILESTONES",
    publishedAt: "2026-07-12",
    updatedAt: "2026-08-04",
    slug: "what-is-the-best-age-for-baby-photos",
    title: "What Is the Best Age for Baby Photos?",
  },
  {
    category: "baby",
    component: WhatHappensDuringBabySession,
    description: "A relaxed look at the play, breaks and family moments that shape a baby session.",
    eyebrow: "THE EXPERIENCE",
    publishedAt: "2026-07-15",
    updatedAt: "2026-08-04",
    slug: "what-happens-during-a-baby-photography-session",
    title: "What Happens During a Baby Photography Session?",
  },
  {
    category: "baby",
    component: HowToPrepareForBabySession,
    description: "A practical photo-day plan for naps, feeding, clothing and familiar comforts.",
    eyebrow: "PHOTO DAY",
    publishedAt: "2026-07-18",
    updatedAt: "2026-08-04",
    slug: "how-to-prepare-for-your-baby-photo-session",
    title: "How to Prepare for Your Baby Photo Session",
  },
  {
    category: "maternity",
    component: BestTimeForMaternityPhotos,
    description: "How to choose a maternity session date that fits your pregnancy, comfort and photo goals.",
    eyebrow: "SESSION TIMING",
    publishedAt: "2026-07-01",
    updatedAt: "2026-08-04",
    slug: "when-is-the-best-time-to-take-maternity-photos",
    title: "When Is the Best Time to Take Maternity Photos?",
  },
  {
    category: "maternity",
    component: WhatToWearForMaternityPhotos,
    description: "Simple, timeless outfit ideas that photograph beautifully and still feel like you.",
    eyebrow: "STYLING GUIDE",
    publishedAt: "2026-07-04",
    updatedAt: "2026-08-04",
    slug: "what-to-wear-for-maternity-photos",
    title: "What to Wear for Maternity Photos",
  },
  {
    category: "maternity",
    component: HowToPrepareForMaternitySession,
    description: "A calm, practical plan for feeling comfortable and prepared on maternity photo day.",
    eyebrow: "SESSION PREP",
    publishedAt: "2026-07-07",
    updatedAt: "2026-08-04",
    slug: "how-to-prepare-for-your-maternity-photo-session",
    title: "How to Prepare for Your Maternity Photo Session",
  },
  {
    category: "newborn",
    component: BestAgeForNewbornPhotos,
    description: "When to schedule your newborn session and what to expect at every age.",
    eyebrow: "SESSION TIMING",
    publishedAt: "2026-06-20",
    updatedAt: "2026-08-02",
    slug: "best-age-for-newborn-photos",
    title: "Best Age for Newborn Photos",
  },
  {
    category: "newborn",
    component: WhatHappensDuringNewbornSession,
    description: "A step-by-step look at how a calm, baby-led newborn session flows.",
    eyebrow: "THE EXPERIENCE",
    publishedAt: "2026-06-23",
    updatedAt: "2026-08-04",
    slug: "what-happens-during-a-newborn-session",
    title: "What Happens During a Newborn Session",
  },
  {
    category: "newborn",
    component: HowToPrepareForNewbornSession,
    description: "How to prepare your home, baby and family for a calm in-home newborn photo session.",
    eyebrow: "PHOTO DAY",
    publishedAt: "2026-06-26",
    updatedAt: "2026-09-07",
    slug: "how-to-prepare-for-your-newborn-session",
    title: "How to Prepare for Your Newborn Photo Session",
  },
  {
    category: "newborn",
    component: NewbornPhotographySafety,
    description: "How gentle posing, hands-on support and composite images keep newborn comfort and safety first.",
    eyebrow: "BABY SAFETY",
    image: newbornPhotographySafetyImage,
    imageAlt: "Newborn baby supported by adult hands during a safe posed photography setup",
    publishedAt: "2026-08-22",
    seoDescription:
      "Learn how safe newborn photography uses baby-led posing, hands-on support, composite images and frequent breaks during Charlotte newborn sessions.",
    seoTitle: "Is Newborn Photography Safe? | Charlotte, NC",
    slug: "newborn-photography-safety",
    title: "Is Newborn Photography Safe? What Parents Should Know",
  },
  {
    category: "newborn",
    component: InHomeNewbornPhotography,
    description: "What to expect when a newborn session is photographed in the comfort of your own home.",
    eyebrow: "IN-HOME EXPERIENCE",
    image: inHomeNewbornImage,
    imageAlt: "Sleeping newborn twins wrapped in neutral blankets inside a heart-shaped prop",
    publishedAt: "2026-08-29",
    seoDescription:
      "Learn how in-home newborn photography works, what Alena brings and how Charlotte families can prepare their space, baby and family for photo day.",
    seoTitle: "In-Home Newborn Photography | Charlotte, NC",
    slug: "in-home-newborn-photography",
    title: "In-Home Newborn Photography: What Parents Should Know",
  },
];

export const resourceArticles: ResourceArticle[] = resourceArticleDefinitions.map((article) => ({
  ...article,
  readTime: resourceReadingTimes[article.slug] ?? "1 min read",
}));

export const findResourceArticle = (category?: string, slug?: string): ResourceArticle | undefined =>
  resourceArticles.find((article) => article.category === category && article.slug === slug);

export const findResourceArticleByHref = (href: string): ResourceArticle | undefined =>
  resourceArticles.find((article) => `/resources/${article.category}/${article.slug}/` === href);

export const formatResourcePublishedAt = (publishedAt: string): string =>
  new Intl.DateTimeFormat("en-US", { day: "numeric", month: "short", year: "numeric" }).format(
    new Date(`${publishedAt}T12:00:00`)
  );
